import { type ReactNode } from 'react';
import { Sparkline } from '@/components/charts/Sparkline';
import { cn } from '@/lib/utils';

/**
 * Reports redesign — KPI card. Mirrors the prototype's
 * `.v1-rep-kpi`: glass card, project-rail color on the left edge,
 * uppercase mono label, large JetBrains numerics for the value, sub
 * line, optional delta pill, optional sparkline anchored top-right.
 *
 * `railIndex` (1-4) drives the rail color so the four KPIs read as a
 * coherent palette (red/blue/green/orange in the prototype). Caller
 * passes `1..4` per slot.
 */
export type KpiDeltaDirection = 'up' | 'down' | 'flat' | 'new';

export interface ReportsKpiCardProps {
  label: string;
  value: ReactNode;
  /** Optional unit suffix rendered at smaller size (e.g. "h"). */
  unit?: string;
  /** Sub-line under the value (e.g. "1240 min total"). */
  sub?: string;
  /** Delta percentage as a string ("12.4%"); pass undefined to skip
   *  the pill. */
  delta?: string;
  /** Direction drives the pill color + arrow glyph. `new` is a
   *  special case: prior period was 0, current > 0. */
  deltaDir?: KpiDeltaDirection;
  /** Daily values for the corner sparkline. Empty array → no spark. */
  sparkValues?: ReadonlyArray<number>;
  sparkColor?: string;
  /** 1..4 — picks the rail color from the four-step palette. */
  railIndex: 1 | 2 | 3 | 4;
}

const RAIL_COLOR: Record<1 | 2 | 3 | 4, string> = {
  1: 'oklch(0.65 0.13 18)', // warm red
  2: 'oklch(0.65 0.13 245)', // workspace blue
  3: 'oklch(0.65 0.13 145)', // green
  4: 'var(--brand-accent)', // brand orange
};

const DELTA_STYLE: Record<KpiDeltaDirection, string> = {
  up: 'bg-[oklch(0.85_0.12_145/0.25)] text-[oklch(0.45_0.15_145)] dark:text-[oklch(0.85_0.15_145)]',
  down: 'bg-[oklch(0.85_0.13_18/0.25)] text-[oklch(0.5_0.18_25)] dark:text-[oklch(0.82_0.16_25)]',
  flat: 'bg-[var(--surface-quiet,var(--secondary))] text-foreground-faint',
  new: 'bg-[oklch(0.74_0.16_50/0.18)] text-[oklch(0.45_0.16_50)] dark:text-[oklch(0.85_0.16_50)]',
};

const DELTA_GLYPH: Record<KpiDeltaDirection, string> = {
  up: '↑',
  down: '↓',
  flat: '–',
  new: '✦',
};

export const ReportsKpiCard = ({
  label,
  value,
  unit,
  sub,
  delta,
  deltaDir = 'flat',
  sparkValues,
  sparkColor,
  railIndex,
}: ReportsKpiCardProps) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-xl border p-4 pt-[14px] backdrop-blur-md',
      'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
    )}
  >
    <span
      aria-hidden="true"
      className="absolute top-[14px] bottom-[14px] left-0 w-[2px] rounded-r-full opacity-55"
      style={{ background: RAIL_COLOR[railIndex] }}
    />

    {sparkValues && sparkValues.length > 0 ? (
      <span className="pointer-events-none absolute right-3 top-[14px] opacity-70">
        <Sparkline values={sparkValues} color={sparkColor} />
      </span>
    ) : null}

    <div
      className={cn(
        'text-foreground-faint flex items-center gap-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em]',
      )}
    >
      {label}
    </div>

    <div
      className={cn(
        'mt-2 text-[30px] font-semibold tracking-[-0.025em] leading-none tabular-nums',
        'text-foreground',
      )}
    >
      {value}
      {unit ? (
        <span className="text-foreground-faint ml-0.5 text-[16px] font-medium">
          {unit}
        </span>
      ) : null}
    </div>

    {sub || delta ? (
      <div className="text-foreground-faint mt-2 flex items-center justify-between gap-2 text-[11.5px]">
        <span>{sub}</span>
        {delta ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-px font-mono text-[10.5px] font-medium',
              DELTA_STYLE[deltaDir],
            )}
          >
            {DELTA_GLYPH[deltaDir]} {delta}
          </span>
        ) : null}
      </div>
    ) : null}
  </div>
);
